import React from 'react';
import { motion } from 'framer-motion';

const Privacy: React.FC = () => {
  // Last updated date
  const lastUpdated = "November 20, 2023";
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };
  
  return (
    <div className="bg-gray-50 pt-16 pb-20 mt-14">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-primary to-secondary py-8 px-6">
            <motion.h1 
              variants={itemVariants}
              className="text-3xl md:text-4xl font-bold text-white"
            >
              Privacy Policy
            </motion.h1>
            <motion.p 
              variants={itemVariants}
              className="text-white/80 mt-2"
            >
              Last updated: {lastUpdated}
            </motion.p>
          </div>
          
          {/* Content */}
          <div className="p-6 md:p-8 space-y-6">
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">1. Introduction</h2>
              <p className="text-gray-600 leading-relaxed">
                Welcome to Recall Nepal. We are committed to protecting your personal information and your right to privacy. This Privacy Policy describes how we collect, use, and share your information when you use our services, websites, and applications.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">2. Information We Collect</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We collect information that you provide directly to us, such as when you:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Create an account or fill out forms on our website</li>
                <li>Subscribe to our newsletter or promotional communications</li>
                <li>Request information about our services</li>
                <li>Contact customer support</li>
                <li>Apply for our programs or scholarships</li>
              </ul>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">3. How We Use Your Information</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Process and manage your application to educational programs</li>
                <li>Provide, maintain, and improve our services</li>
                <li>Send you technical notices, updates, security alerts, and support messages</li>
                <li>Respond to your comments, questions, and customer service requests</li>
                <li>Communicate with you about products, services, offers, and events</li>
                <li>Monitor and analyze trends, usage, and activities in connection with our services</li>
              </ul>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">4. Information Sharing</h2>
              <p className="text-gray-600 leading-relaxed">
                We do not sell, rent, or share your personal information with third parties without your consent, except as described in this policy. We may share your personal information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 mt-4">
                <li>Educational institutions to which you apply through our services</li>
                <li>Service providers who perform services on our behalf</li>
                <li>Professional advisors, such as lawyers, auditors, and insurers</li>
                <li>Government authorities when required by law</li>
              </ul>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">5. Data Security</h2>
              <p className="text-gray-600 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized or unlawful processing, accidental loss, destruction, or damage. However, no method of transmission over the Internet or electronic storage is 100% secure. Therefore, while we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">6. Your Rights</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>The right to access and receive a copy of your personal information</li>
                <li>The right to rectify or update your personal information</li>
                <li>The right to erase your personal information</li>
                <li>The right to restrict or object to our processing of your personal information</li>
                <li>The right to data portability</li>
              </ul>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">7. Changes to This Privacy Policy</h2>
              <p className="text-gray-600 leading-relaxed">
                We may update this Privacy Policy from time to time in response to changing legal, technical, or business developments. When we update our Privacy Policy, we will take appropriate measures to inform you, consistent with the significance of the changes we make.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">8. Contact Us</h2>
              <p className="text-gray-600 leading-relaxed">
                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                <p className="text-gray-800 font-medium">Recall Nepal</p>
                <p className="text-gray-600">Kathmandu, Nepal</p>
                <p className="text-gray-600">Email: privacy@recallnepal.com</p>
                <p className="text-gray-600">Phone: +977-1-XXXXXXX</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy; 